import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface ComplianceMetrics {
  overallScore: number;
  facilityCompliance: number;
  productCompliance: number;
  safetyCompliance: number;
  reportingCompliance: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  lastAuditDate: string;
  nextAuditDate: string;
  totalViolations: number;
  openViolations: number;
  resolvedViolations: number;
}

interface BusinessMetrics {
  totalRevenue: number;
  monthlyGrowth: number;
  facilityCount: number;
  productCount: number;
  employeeCount: number;
  marketShare: number;
  customerSatisfaction: number;
}

interface RiskAssessment {
  productRisks: Array<{
    productName: string;
    riskScore: number;
    riskFactors: string[];
  }>;
  facilityRisks: Array<{
    facilityName: string;
    riskScore: number;
    riskFactors: string[];
  }>;
  regulatoryRisks: Array<{
    regulation: string;
    complianceStatus: 'COMPLIANT' | 'NON_COMPLIANT' | 'PENDING';
    deadline: string;
    impact: 'LOW' | 'MEDIUM' | 'HIGH';
  }>;
}

interface DashboardData {
  complianceMetrics: ComplianceMetrics;
  businessMetrics: BusinessMetrics;
  riskAssessment: RiskAssessment;
  recentActivities: Array<{
    id: number;
    type: 'FACILITY_REGISTERED' | 'PRODUCT_ADDED' | 'SAE_REPORTED' | 'VIOLATION_FOUND' | 'COMPLIANCE_AUDIT';
    description: string;
    timestamp: string;
    severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  }>;
  upcomingDeadlines: Array<{
    id: number;
    type: string;
    description: string;
    deadline: string;
    daysRemaining: number;
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  }>;
}

const EnterpriseDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedView, setSelectedView] = useState('overview');


  useEffect(() => {
    fetchDashboardData();
  }, [selectedTimeframe]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Simulate enterprise data - in real app, this would come from backend
      const mockData: DashboardData = {
        complianceMetrics: {
          overallScore: 87,
          facilityCompliance: 92,
          productCompliance: 85,
          safetyCompliance: 89,
          reportingCompliance: 78,
          riskLevel: 'MEDIUM',
          lastAuditDate: '2024-01-15',
          nextAuditDate: '2024-04-15',
          totalViolations: 12,
          openViolations: 3,
          resolvedViolations: 9
        },
        businessMetrics: {
          totalRevenue: 2500000,
          monthlyGrowth: 12.5,
          facilityCount: 8,
          productCount: 156,
          employeeCount: 450,
          marketShare: 3.2,
          customerSatisfaction: 4.6
        },
        riskAssessment: {
          productRisks: [
            { productName: 'Retinol Serum Pro', riskScore: 85, riskFactors: ['High retinol content', 'Sensitive skin warnings'] },
            { productName: 'Anti-Aging Cream', riskScore: 72, riskFactors: ['Peptide complex', 'Clinical testing required'] },
            { productName: 'Sunscreen SPF 50', riskScore: 45, riskFactors: ['UV filter compliance'] }
          ],
          facilityRisks: [
            { facilityName: 'Main Manufacturing Plant', riskScore: 65, riskFactors: ['Equipment maintenance', 'Staff training'] },
            { facilityName: 'Distribution Center', riskScore: 35, riskFactors: ['Temperature monitoring'] }
          ],
          regulatoryRisks: [
            { regulation: 'MoCRA Facility Registration', complianceStatus: 'COMPLIANT', deadline: '2024-12-31', impact: 'HIGH' },
            { regulation: 'Product Listing', complianceStatus: 'PENDING', deadline: '2024-06-30', impact: 'MEDIUM' },
            { regulation: 'SAE Reporting', complianceStatus: 'COMPLIANT', deadline: 'Ongoing', impact: 'HIGH' }
          ]
        },
        recentActivities: [
          { id: 1, type: 'SAE_REPORTED', description: 'Serious adverse event reported for Retinol Serum Pro', timestamp: '2024-01-20T10:30:00Z', severity: 'CRITICAL' },
          { id: 2, type: 'FACILITY_REGISTERED', description: 'New manufacturing facility registered in Texas', timestamp: '2024-01-19T14:20:00Z', severity: 'INFO' },
          { id: 3, type: 'COMPLIANCE_AUDIT', description: 'Quarterly compliance audit completed - Score: 87/100', timestamp: '2024-01-18T09:15:00Z', severity: 'INFO' },
          { id: 4, type: 'VIOLATION_FOUND', description: 'Minor labeling violation found in Product X', timestamp: '2024-01-17T16:45:00Z', severity: 'WARNING' }
        ],
        upcomingDeadlines: [
          { id: 1, type: 'Facility Renewal', description: 'Main Plant registration renewal', deadline: '2024-02-15', daysRemaining: 25, priority: 'HIGH' },
          { id: 2, type: 'Product Listing', description: 'New product line registration', deadline: '2024-03-01', daysRemaining: 39, priority: 'MEDIUM' },
          { id: 3, type: 'Safety Assessment', description: 'Annual safety review for premium products', deadline: '2024-04-01', daysRemaining: 70, priority: 'MEDIUM' }
        ]
      };

      setDashboardData(mockData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 80) return '#ef4444'; // Red
    if (score >= 60) return '#f59e0b'; // Yellow
    if (score >= 40) return '#3b82f6'; // Blue
    return '#10b981'; // Green
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800';
      case 'ERROR': return 'bg-orange-100 text-orange-800';
      case 'WARNING': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'border-red-500 bg-red-50';
      case 'HIGH': return 'border-orange-500 bg-orange-50';
      case 'MEDIUM': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-green-500 bg-green-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Enterprise Dashboard...</h2>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div>Error loading dashboard data</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Enterprise Compliance Dashboard</h1>
              <p className="text-gray-600 mt-2">Comprehensive MoCRA compliance management for enterprise organizations</p>
            </div>
            <div className="flex space-x-4">
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
              </select>
              <select
                value={selectedView}
                onChange={(e) => setSelectedView(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="overview">Overview</option>
                <option value="compliance">Compliance</option>
                <option value="risks">Risk Assessment</option>
                <option value="business">Business Metrics</option>
              </select>
            </div>
          </div>
        </div>

        {/* Information Board */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-purple-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Enterprise MoCRA Compliance</h3>
              <div className="text-purple-800 space-y-2">
                <p><strong>MoCRA (Modernization of Cosmetics Regulation Act):</strong> Comprehensive FDA regulation for cosmetic safety and compliance.</p>
                <p><strong>Enterprise Features:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Multi-facility compliance monitoring</li>
                  <li>Centralized adverse event reporting</li>
                  <li>Automated regulatory submissions</li>
                  <li>Risk assessment and mitigation</li>
                </ul>
                <p><strong>Key Requirements:</strong> Facility registration, product listing, adverse event reporting, safety substantiation, and record keeping.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Compliance Score</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.complianceMetrics.overallScore}%</p>
                <p className="text-xs text-gray-500">+5% from last month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Monthly Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${(dashboardData.businessMetrics.totalRevenue / 1000000).toFixed(1)}M</p>
                <p className="text-xs text-green-500">+{dashboardData.businessMetrics.monthlyGrowth}% growth</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Open Violations</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.complianceMetrics.openViolations}</p>
                <p className="text-xs text-red-500">Requires attention</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Risk Level</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.complianceMetrics.riskLevel}</p>
                <p className="text-xs text-purple-500">Monitored daily</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Score Chart */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Trend Analysis</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[
              { month: 'Oct', overall: 82, facility: 88, product: 79, safety: 85 },
              { month: 'Nov', overall: 84, facility: 90, product: 81, safety: 87 },
              { month: 'Dec', overall: 86, facility: 91, product: 83, safety: 88 },
              { month: 'Jan', overall: 87, facility: 92, product: 85, safety: 89 }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="overall" stroke="#3b82f6" strokeWidth={3} />
              <Line type="monotone" dataKey="facility" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="product" stroke="#f59e0b" strokeWidth={2} />
              <Line type="monotone" dataKey="safety" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Assessment and Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Risk Assessment */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
            <div className="space-y-4">
              {dashboardData.riskAssessment.productRisks.map((risk, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-900">{risk.productName}</h4>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: getRiskColor(risk.riskScore) }}
                    >
                      {risk.riskScore}/100
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {risk.riskFactors.map((factor, factorIndex) => (
                      <span key={factorIndex} className="px-2 py-1 bg-gray-200 text-gray-800 text-xs rounded font-medium">
                        {factor}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activities */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
            <div className="space-y-3">
              {dashboardData.recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.severity === 'CRITICAL' ? 'bg-red-500' :
                    activity.severity === 'ERROR' ? 'bg-orange-500' :
                    activity.severity === 'WARNING' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.description}</p>
                    <p className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleString()}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(activity.severity)}`}>
                    {activity.severity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardData.upcomingDeadlines.map((deadline) => (
              <div key={deadline.id} className={`border-l-4 p-4 rounded-lg ${getPriorityColor(deadline.priority)}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">{deadline.type}</h4>
                    <p className="text-sm text-gray-600">{deadline.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Due: {new Date(deadline.deadline).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900">{deadline.daysRemaining}</span>
                    <p className="text-xs text-gray-500">days left</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseDashboard;

